import type React from 'react';

export type ButtonVariant = 'primary' | 'primary-elevated' | 'secondary' | 'tertiary' | 'neutral' | 'input';
export type ButtonSize = 'large' | 'medium' | 'small' | 'xsmall';
export type ButtonState = 'enabled' | 'disabled' | 'pressed';

export interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'disabled'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  state?: ButtonState;
  children?: React.ReactNode;
  fullWidth?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  iconOnly?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
}

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'medium',
  state = 'enabled',
  children,
  fullWidth = false,
  icon,
  iconPosition = 'right',
  iconOnly = false,
  className = '',
  onClick,
  disabled = false,
  isLoading = false,
  ...props
}) => {
  // Derive state from disabled and isLoading props if state is not explicitly set
  const derivedState = (disabled || isLoading) ? 'disabled' : state;
  const getButtonClasses = () => {
    const baseClasses = [
      'inline-flex',
      'items-center',
      'justify-center',
      'font-manrope',
      'font-bold',
      'text-center',
      'transition-all',
      'duration-200',
      'ease-out',
      'overflow-hidden',
      'relative',
      'transform-gpu',
      'will-change-transform'
    ];

    // Size classes
    const sizeClasses = {
      large: [
        'h-16',
        'min-w-[64px]',
        'px-3',
        'text-[19px]',
        'leading-[24.7px]',
        'rounded-[28px]',
        'gap-1'
      ],
      medium: [
        'h-14',
        'min-w-[56px]',
        'px-3',
        'text-[19px]',
        'leading-[24.7px]',
        'rounded-[24px]',
        'gap-0.5'
      ],
      small: [
        'h-12',
        'min-w-[48px]',
        'px-2.5',
        'text-base',
        'leading-[20.8px]',
        'rounded-[20px]',
        'gap-0.5'
      ],
      xsmall: [
        'h-10',
        'min-w-[40px]',
        'px-2',
        'text-base',
        'leading-[20.8px]',
        'rounded-2xl'
      ]
    };

    // Variant and state classes
    const variantStateClasses = {
      primary: {
        enabled: [
          'bg-gradient-to-r',
          'from-primary-stop-1',
          'to-primary-stop-2',
          'text-white',
          'hover:shadow-primary-glow',
          'hover:from-[#E04A1C]',
          'hover:to-[#E8911D]',
          'transition-all',
          'duration-200'
        ],
        disabled: [
          'bg-disabled-fill',
          'text-content-muted',
          'cursor-not-allowed'
        ],
        pressed: [
          'bg-gradient-to-r',
          'from-pressed-crimson',
          'to-pressed-tomato',
          'text-white',
          'shadow-pressed-glow',
          'scale-[0.98]'
        ]
      },
      'primary-elevated': {
        enabled: [
          'bg-gradient-to-r',
          'from-primary-stop-1',
          'to-primary-stop-2',
          'text-white',
          'shadow-primary-elevated',
          'hover:shadow-primary-glow',
          'hover:from-[#E04A1C]',
          'hover:to-[#E8911D]',
          'hover:-translate-y-0.5',
          'transition-all',
          'duration-200'
        ],
        disabled: [
          'bg-disabled-fill',
          'text-content-muted',
          'cursor-not-allowed'
        ],
        pressed: [
          'bg-gradient-to-r',
          'from-pressed-crimson',
          'to-pressed-tomato',
          'text-white',
          'shadow-pressed-glow',
          'scale-[0.98]'
        ]
      },
      secondary: {
        enabled: [
          'bg-gradient-to-r',
          'from-secondary-stop-1',
          'to-secondary-stop-2',
          'text-action-key-darker',
          'hover:shadow-secondary-glow',
          'hover:from-[#FFCBA6]',
          'hover:to-[#FFE1A6]',
          'transition-all',
          'duration-200'
        ],
        disabled: [
          'bg-disabled-fill',
          'text-content-muted',
          'cursor-not-allowed'
        ],
        pressed: [
          'bg-gradient-to-r',
          'from-[#FFCBA6]',
          'to-[#FFE1A6]',
          'text-action-key-darker',
          'shadow-secondary-pressed-glow'
        ]
      },
      tertiary: {
        enabled: [
          'bg-transparent',
          'text-action-key-default',
          'hover:bg-gradient-to-r',
          'hover:from-secondary-stop-1',
          'hover:to-secondary-stop-2',
          'hover:text-action-key-darker',
          'transition-all',
          'duration-200'
        ],
        disabled: [
          'bg-transparent',
          'text-content-muted',
          'cursor-not-allowed'
        ],
        pressed: [
          'bg-gradient-to-r',
          'from-secondary-stop-1',
          'to-secondary-stop-2',
          'text-action-key-darker'
        ]
      },
      neutral: {
        enabled: [
          'bg-transparent',
          'text-content-tertiary',
          'hover:bg-neutral-fill-lighter',
          'hover:text-content-secondary',
          'transition-all',
          'duration-200'
        ],
        disabled: [
          'bg-transparent',
          'text-content-muted',
          'cursor-not-allowed'
        ],
        pressed: [
          'bg-neutral-fill-lighter',
          'text-content-secondary'
        ]
      },
      input: {
        enabled: [
          'bg-gradient-to-r',
          'from-[#FFF7FC]',
          'to-[#FFF9F2]',
          'text-[#E52284]',
          'border-[1.5px]',
          'border-ai-vib-stop-1',
          'min-w-[200px]',
          'px-5',
          'hover:shadow-lg',
          'hover:from-[#FFF0F8]',
          'hover:to-[#FFF6EE]',
          'hover:border-2',
          'transition-all',
          'duration-200'
        ],
        disabled: [
          'bg-neutral-fill-lighter',
          'text-content-muted',
          'border-[1.5px]',
          'border-content-muted',
          'cursor-not-allowed',
          'min-w-[200px]',
          'px-5'
        ],
        pressed: [
          'bg-gradient-to-r',
          'from-[#FFCCEE]',
          'to-[#FFDFBF]',
          'text-[#E52284]',
          'border-[1.5px]',
          'border-ai-vib-stop-1',
          'min-w-[200px]',
          'px-5'
        ]
      }
    };

    const widthClasses = fullWidth ? ['w-full'] : [];

    return [
      ...baseClasses,
      ...sizeClasses[size],
      ...variantStateClasses[variant][derivedState],
      ...widthClasses,
      className
    ].filter(Boolean).join(' ');
  };

  const getLabelPadding = () => {
    if (iconOnly) {return '';}
    
    const paddingMap = {
      large: 'px-2',
      medium: 'px-1.5',
      small: 'px-1',
      xsmall: 'px-1'
    };
    return paddingMap[size];
  };

  const isDisabled = derivedState === 'disabled';
  const isPressed = derivedState === 'pressed';

  return (
    <button
      className={getButtonClasses()}
      disabled={isDisabled}
      onClick={isDisabled ? undefined : onClick}
      data-pressed={isPressed}
      {...props}
    >
      {iconOnly ? (
        <div className="flex items-center justify-center">
          {isLoading ? (
            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
          ) : (
            icon
          )}
        </div>
      ) : (
        <div className={`flex items-center justify-center ${getLabelPadding()}`}>
          {isLoading && (
            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
          )}
          {!isLoading && icon && iconPosition === 'left' && (
            <span className="mr-2">{icon}</span>
          )}
          {children && <span className="break-words">{children}</span>}
          {!isLoading && icon && iconPosition === 'right' && (
            <span className="ml-2">{icon}</span>
          )}
        </div>
      )}
    </button>
  );
};

export default Button;